/**
 * Course Rating Component
 * Allows logged-in users to rate courses (1-5 stars)
 */

import React, { useEffect, useState } from 'react';
import { Box, Typography, Rating, Button, Alert, CircularProgress, Link } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import LoginIcon from '@mui/icons-material/Login';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchCourseRatings, fetchUserRating, submitRating, removeRating } from '../../store/thunks/ratingsThunks';
import { Course } from '@e-learning/shared/src/store/types';
import LoginModal from '../auth/LoginModal';

interface CourseRatingProps {
  course: Course;
}

const CourseRating: React.FC<CourseRatingProps> = ({ course }) => {
  const dispatch = useAppDispatch();
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const { courseRatings, userRatings, loading, error } = useAppSelector((state) => state.ratings);
  
  const [hover, setHover] = useState(-1);
  const [submitting, setSubmitting] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  const ratings = courseRatings[course.id] || [];
  const userRating = userRatings[course.id];
  const averageRating = course.averageRating || 0;
  const totalRatings = course.totalRatings || ratings.length;

  useEffect(() => {
    // Fetch ratings for this course
    dispatch(fetchCourseRatings(course.id));
    
    // Fetch user's rating if authenticated
    if (isAuthenticated && user) {
      dispatch(fetchUserRating({ courseId: course.id, userId: user.uid }));
    }
  }, [course.id, dispatch, isAuthenticated, user]);

  const handleRatingChange = async (newValue: number | null) => {
    if (!isAuthenticated || !user || !newValue) {
      return;
    }

    setSubmitting(true);
    try {
      await dispatch(
        submitRating({
          courseId: course.id,
          userId: user.uid,
          rating: newValue,
        })
      ).unwrap();
    } catch (err) {
      console.error('Failed to submit rating:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveRating = async () => {
    if (!isAuthenticated || !user) {
      return;
    }

    setSubmitting(true);
    try {
      await dispatch(removeRating({ courseId: course.id, userId: user.uid })).unwrap();
    } catch (err) {
      console.error('Failed to remove rating:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <Box sx={{ mb: 3 }}>
        <Alert 
          severity="info" 
          sx={{ 
            mb: 2,
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: '100%',
            },
          }}
        >
          <Typography variant="body2">
            Please{' '}
            <Link
              component="button"
              variant="body2"
              onClick={() => setLoginModalOpen(true)}
              sx={{
                color: 'primary.light',
                textDecoration: 'underline',
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            >
              log in
            </Link>
            {' '}to rate this course
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<LoginIcon />}
            onClick={() => setLoginModalOpen(true)}
            sx={{
              ml: 'auto',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              },
            }}
          >
            Login
          </Button>
        </Alert>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Rating value={averageRating} readOnly precision={0.1} size="large" />
          <Typography variant="body2" color="text.secondary">
            ({totalRatings} {totalRatings === 1 ? 'rating' : 'ratings'})
          </Typography>
        </Box>
        <LoginModal
          open={loginModalOpen}
          onClose={() => setLoginModalOpen(false)}
          onSuccess={() => {
            // User successfully logged in, modal will close automatically
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Rate this course:
        </Typography>
        {submitting && <CircularProgress size={20} />}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Rating
          value={userRating?.rating || 0}
          onChange={(_, newValue) => handleRatingChange(newValue)}
          onChangeActive={(_, newHover) => setHover(newHover)}
          precision={1}
          size="large"
          disabled={submitting}
        />
        {userRating && (
          <Button
            size="small"
            variant="outlined"
            onClick={handleRemoveRating}
            disabled={submitting}
            sx={{ ml: 2 }}
          >
            Remove Rating
          </Button>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <StarIcon sx={{ color: 'warning.main', fontSize: 20 }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {averageRating.toFixed(1)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          ({totalRatings} {totalRatings === 1 ? 'rating' : 'ratings'})
        </Typography>
      </Box>
    </Box>
  );
};

export default CourseRating;

